import { IconButton, Text } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useReducer } from "react";
import type { TrainingEvalRunFilters, TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { hatcheryTextModes } from "@/app/theme";
import { PaginationFooter } from "@/components/browse";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import type { SelectedPolicyRef } from "@/modules/training/public";
import { useRelativeTimeClock } from "@/utils/useRelativeTimeClock";
import { AxisFilterBar, AxisFilterChips, FilterResultCount } from "@/components/filter";
import { EvalRunTableSource } from "./EvalRunTableSource";
import { buildEvalRunColumns, evaluationResultsEmptyMessage } from "./evalRunTableColumns";
import type { EvalRunTableRow } from "./EvalRunTableRow";
import {
  chipsToFilters,
  EVAL_RUN_FILTER_FIELDS,
  type EvalRunFilterChip,
} from "./evalRunFilterFields";
import { CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";
import { isCheckpointSortable } from "@/components/table/CheckpointCount";
import tableStyles from "@/components/table/dataTableSelection.module.css";
import headerStyles from "@/components/table/tablePanelHeader.module.css";
import styles from "./EvaluationResultsPanel.module.css";

// evaluationResults 전체화면 창 안의 Evaluation 결과 표다. 모든 축은 props 로 받는다. 창 콘텐츠가
// 칩, 페이지 크기, 정책 초점을 창 로컬 상태로 소유한다. 이 패널은 그 값을 렌더하고 변경을
// 알린다. 자기 사본은 가지지 않는다.
//
// selectedPolicies 는 표를 창의 정책 조합으로 좁힌다. 조합은 창의 identity 라서 여기서 줄지
// 않는다. 카드는 한 번에 정책 하나에 *초점*만 둔다. 클릭하면 좁히고, 다시 클릭하면 조합
// 전체로 돌아간다.
export function EvaluationResultsPanel({
  source,
  pageSize,
  onPageSizeChange,
  chips,
  onChipsChange,
  baseFilters,
  selectedPolicies,
  focusedPolicyId,
  onFocusPolicyChange,
  onCompareRuns,
  onMergeRuns,
  viewSlots,
}: {
  // 창 콘텐츠가 만들고 유지한다(createEvalRunTableSource). 그래서 로드한 페이지, 페이지 캐시,
  // run 선택이 최소화와 복원 뒤에도 유지된다. 최소화하면 셸이 이 패널을 언마운트하고, 아래의
  // 언마운트 처리가 폴링을 멈춘다(rules.md §12). 이 패널은 소스의 축을 바꾸지만, 받은 소스를
  // dispose 하지 않는다.
  source: EvalRunTableSource;
  // 페이지당 행 수다. 창 콘텐츠가 이 설정을 소유하므로 이 패널이 remount 해도 유지된다.
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  // 필터 칩이다. 창 콘텐츠가 소유한다. 이 패널은 다음 칩 전체 목록을 알린다.
  chips: readonly EvalRunFilterChip[];
  onChipsChange: (next: readonly EvalRunFilterChip[]) => void;
  // 창을 열 때의 member/experiment 스냅샷이다. 칩 다음에 서버 필터에 합친다.
  baseFilters: Pick<TrainingEvalRunFilters, "memberUuid" | "experimentId">;
  selectedPolicies: readonly SelectedPolicyRef[];
  // 카드로 고른 초점 정책이다. null 이면 조합 전체를 표시한다. 소유자(창 콘텐츠)가 보관하므로
  // 최소화해도 유지된다.
  focusedPolicyId: string | null;
  onFocusPolicyChange: (policyId: string | null) => void;
  onCompareRuns: (runs: readonly TrainingEvalRunSummaryView[]) => void;
  onMergeRuns: (runs: readonly TrainingEvalRunSummaryView[]) => void;
  // 열린 비교·병합 뷰의 행 색 슬롯이다. 여러 run 을 보는 뷰가 없으면 비어 있다.
  viewSlots: Readonly<Record<string, number>>;
}) {
  const policyFilterActive = selectedPolicies.length > 0;
  // 정책 필터가 있으면 정책이 주 조회 축이다. 그래서 세션 축은 필터 바에서 뺀다. 창의 칩은 빈
  // 상태로 시작하고 뺀 축은 다시 추가할 수 없다. 그래서 숨겨야 할 기존 세션 칩은 없다.
  const filterFields = policyFilterActive
    ? EVAL_RUN_FILTER_FIELDS.filter((field) => field.key !== "session")
    : EVAL_RUN_FILTER_FIELDS;
  // 칩과 창 스냅샷 축을 한 객체로 합쳐 setFilters 를 한 번만 호출한다. 소스의 동등성 비교는
  // JSON.stringify 라서 키 순서에 영향을 받는다. 그래서 생성자의 initialFilters 와 spread 순서를 맞춘다.
  const filters = useMemo(
    () => ({ ...chipsToFilters(chips), ...baseFilters }),
    [chips, baseFilters],
  );

  // 초점 정책이 있으면 그 정책 하나로, 없으면 조합 전체로 좁힌다. 초점이 조합에 없는 값이면
  // 조합으로 되돌린다. 이 방어 코드는 빈 필터로 전체 run 을 여는 것을 막는다.
  const effectivePolicies = useMemo(() => {
    if (focusedPolicyId === null) {
      return selectedPolicies;
    }
    const focused = selectedPolicies.filter((policy) => policy.policyId === focusedPolicyId);
    return focused.length > 0 ? focused : selectedPolicies;
  }, [selectedPolicies, focusedPolicyId]);

  // 빈 선택의 정규화는 소스가 한다. 이 effect 는 합친 값을 전달하기만 한다.
  useEffect(() => {
    void source.setPolicyFilter(effectivePolicies);
  }, [source, effectivePolicies]);

  useEffect(() => {
    void source.setFilters(filters);
  }, [source, filters]);

  // 행 수를 소스에 전달한다. 마운트 때는 아무것도 하지 않는다. 소스가 이미 이 크기를 가진다.
  useEffect(() => {
    void source.setPageSize(pageSize);
  }, [source, pageSize]);

  // 소스가 로드를 끝내거나 페이지를 바꾸면 페이지 정보를 갱신한다. 언마운트(창 최소화)는
  // 폴링만 멈춘다. 창 콘텐츠가 소스의 수명을 소유하므로 여기서 로드한 데이터는 버리지 않는다.
  // 복원할 때 initialize() 가 다시 로드하지 않고 폴링을 재개한다.
  const [, bumpVersion] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    source.setActive(true);
    void source.initialize();
    const unsubscribe = source.subscribe(bumpVersion);
    return () => {
      unsubscribe();
      source.setActive(false);
    };
  }, [source]);

  const nowMs = useRelativeTimeClock();
  const snapshot = source.getSnapshot();
  const checkpointSortable = isCheckpointSortable(
    snapshot.rows,
    source.sortedColumnKey === "checkpointLabel",
  );
  const columns = useMemo(
    () => buildEvalRunColumns(nowMs, checkpointSortable),
    [nowMs, checkpointSortable],
  );
  const selectedCount = snapshot.selectedIds.size;
  const isLoading = snapshot.loadState === "loading";
  // 행 색상은 열려 있는 비교·병합 뷰의 슬롯을 따른다. 뷰 상태는 창 콘텐츠가 소유한다. 이 패널은
  // 같은 슬롯 객체를 prop 으로 받는다. 그래서 뷰가 닫히면 색도 함께 초기화된다.
  const compareRowClassName = (row: EvalRunTableRow): string | undefined => {
    const slot = viewSlots[row.id];
    if (slot === undefined || !snapshot.selectedIds.has(row.id)) {
      return undefined;
    }
    return `${styles.comparedRun} ${styles[`compareSlot${slot % CHART_SERIES_PALETTE.length}`]}`;
  };

  return (
    <section className={styles.panel} aria-label="Evaluation 결과">
      <div className={styles.headerStack}>
        {/* 아래 표는 이 정책들의 run 만 가진다. 조합은 창의 identity 라서 카드 클릭은 *초점*만
            둔다. 클릭하면 표를 그 정책으로 좁히고, 다시 클릭하면 조합 전체로 돌아간다. 정책을
            제거하거나 모든 run 을 여는 동작은 없다. 정책이 하나면 카드가 필요 없다. 창 제목이
            이미 그 정책 이름을 표시한다. */}
        {selectedPolicies.length > 1 ? (
          <div className={styles.policyCards} role="group" aria-label="선택한 Policy">
            {selectedPolicies.map((policy) => {
              const focused = policy.policyId === focusedPolicyId;
              return (
                <button
                  key={policy.policyId}
                  type="button"
                  className={`${styles.policyCard} ${focused ? styles.policyCardFocused : ""}`}
                  aria-pressed={focused}
                  // 이름을 함께 전달한다. 카드 폭 때문에 잘린 이름을 볼 수 있는 유일한 방법이다.
                  title={
                    focused
                      ? `${policy.policyName} — 클릭하면 선택한 모든 Policy 의 Evaluation 실행을 다시 보여줍니다.`
                      : `${policy.policyName} — 클릭하면 이 Policy 의 Evaluation 실행만 보여줍니다.`
                  }
                  onClick={() => onFocusPolicyChange(focused ? null : policy.policyId)}
                >
                  <Text variant={hatcheryTextModes["body-bold"]} className={styles.policyCardName}>
                    {policy.policyName}
                  </Text>
                </button>
              );
            })}
          </div>
        ) : null}
        {/* 1줄은 필터 입력, 2줄은 확정 칩(있을 때만)이다. Policy 카탈로그 패널과 같은 구성이다.
            선택 액션은 표 위 도구 띠(BulkActionBar)에 있다. */}
        <div className={headerStyles.toolbar}>
          <div className={headerStyles.filters}>
            <AxisFilterBar fields={filterFields} chips={chips} onChange={onChipsChange} />
          </div>
          <AxisFilterChips fields={filterFields} chips={chips} onChange={onChipsChange} />
        </div>
      </div>
      {/* host 다. 표 상단 도구 띠(액션·결과 수/선택 개수·해제)를 표와 한 요소로 묶는다. 세션·Policy
          카탈로그 표와 같은 구조다(rules.md §17). 자식은 띠와 표 두 개뿐이다. */}
      <div className={`${styles.tableArea} ${tableStyles.selectionColumn} ${bulkBarStyles.host}`}>
        <BulkActionBar
          source={source}
          ariaLabel="Evaluation 결과 도구"
          idleContent={
            snapshot.loadState === "loaded" ? (
              <FilterResultCount count={snapshot.total} unitLabel="Evaluation 실행" />
            ) : null
          }
          // 액션은 항상 표시해서 선택으로 무엇을 할 수 있는지 알린다. 활성 상태만 선택 수에 따라 바뀐다.
          // 비교와 통합은 실행이 두 개 이상일 때 의미가 있다.
          actions={
            <>
              <IconButton
                text="비교"
                type="button"
                size="sm"
                variant="solid"
                disabled={selectedCount < 2}
                title={
                  selectedCount < 2
                    ? "비교하려면 Evaluation 실행을 두 개 이상 선택해 주세요."
                    : undefined
                }
                onClick={() => onCompareRuns(source.getSelectedRows().map((row) => row.getRun()))}
              />
              <IconButton
                text="통합"
                type="button"
                size="sm"
                variant="solid"
                disabled={selectedCount < 2}
                title={
                  selectedCount < 2
                    ? "통합하려면 Evaluation 실행을 두 개 이상 선택해 주세요."
                    : undefined
                }
                onClick={() => onMergeRuns(source.getSelectedRows().map((row) => row.getRun()))}
              />
            </>
          }
          // 새로고침 버튼이다(띠 우측, sm, rules.md §17). 이 표는 스스로도 갱신한다(실행 중 행이
          // 있으면 10초, 아니면 60초). 주기를 기다리지 않고 지금 갱신하려는 사용자를 위해 버튼을 둔다.
          rightContent={
            <IconButton
              size="sm"
              text={isLoading ? "로딩 중…" : "새로고침"}
              variant="surface"
              disabled={isLoading}
              onClick={() => void source.refresh()}
            />
          }
        />
        <HatDataTable
          source={source}
          columns={columns}
          emptyMessage={evaluationResultsEmptyMessage(
            Object.keys(filters).length > 0,
            policyFilterActive,
          )}
          loadingMessage="Evaluation 결과를 불러오는 중입니다."
          ariaLabel="Evaluation 결과"
          scrollable
          minWidth="0px"
          rowClassName={compareRowClassName}
          showCheckbox
          showIndex={false}
        />
      </div>
      <PaginationFooter
        page={snapshot.page}
        size={snapshot.size}
        total={snapshot.total}
        isLoading={snapshot.loadState === "loading"}
        ariaLabel="Evaluation 결과 페이지 이동"
        mode="compact"
        hideWhenSinglePage
        // 현재 크기는 소스에서 온다. 그래서 선택기 값은 표와 다를 수 없다.
        onPageSizeChange={onPageSizeChange}
        onPrevious={() => void source.goToPage(Math.max(1, snapshot.page - 1))}
        onNext={() => void source.goToPage(snapshot.page + 1)}
      />
    </section>
  );
}
